import Phaser from 'phaser';
import { spriteSize, tileSize } from '../consts/sizes';

import { MoveMap, Moves } from '~/src/game/grid/grid';
import { red } from './colors';
import { EVENT_GAME_UPDATESCORE, EVENT_PACMAN_HASPOWER, sceneEvents } from '../events/GameEvents';
import { DOT_POINTS, ENERGIZER_POINTS, Fruit, GHOST_SCARE_TIMER } from '../consts/config';
import { calculateSpeed } from './ghost-ai/GhostAI';
import { boardCollision } from './colliders/board';

enum HeroState {
	Normal,
	Powered
}
export default class Hero extends Phaser.Physics.Arcade.Sprite {
	private heroState = HeroState.Normal;

	private poweredAccumulator = 0;
	private audioSprite: Phaser.Sound.HTML5AudioSound | Phaser.Sound.WebAudioSound;

	private sensor: Phaser.GameObjects.Rectangle;
	public speed = 0;

	private currentDirection = Moves.None;
	private moveDirection = Moves.None;
	private nextSnap: { move: Moves, pos: Phaser.Math.Vector2 } | null = null;
	private snapFrameCounter = 0;
	private turningDisabled = 0;
	private eatSoundIndex = 0;

	get isPowered() {
		return this.heroState === HeroState.Powered;
	}

	get facingVector() {
		return new Phaser.Math.Vector2().setToPolar(this.rotation);
	}

	constructor(scene: Phaser.Scene, pos: Required<Phaser.Types.Math.Vector2Like>, texture: string, level: number) {
		super(scene, pos.x, pos.y, texture);
		this.speed = 1 + calculateSpeed(level) * tileSize;

		this.play('hero-idle');

		this.sensor = scene.add.rectangle(pos.x, pos.y, spriteSize, spriteSize, new Phaser.Display.Color(255, 0, 255).color, 0.8);
		this.sensor.setOrigin(0.5, 0.5);
		this.sensor.setVisible(!!scene.game.config.physics.arcade?.debug);

		this.audioSprite = this.scene.sound.addAudioSprite('sfx');
	}

	canEatDot(dot: Phaser.Physics.Arcade.Sprite) {
		const heroPos = this.body.center;

		const dotPos = (dot.body as Phaser.Physics.Arcade.Body).center.clone();
		return Phaser.Math.Distance.BetweenPointsSquared(heroPos, dotPos) <= spriteSize;
	}

	eat(dot: Phaser.Physics.Arcade.Sprite) {
		this.audioSprite.play(`eat${this.eatSoundIndex}`);
		this.eatSoundIndex = (this.eatSoundIndex + 1) % 2;
		dot.destroy(true);
	}
	eatDot(dot: Phaser.Physics.Arcade.Sprite) {
		sceneEvents.emit(EVENT_GAME_UPDATESCORE, DOT_POINTS);
		this.eat(dot);
	}

	eatPowerDot(dot: Phaser.Physics.Arcade.Sprite) {
		sceneEvents.emit(EVENT_GAME_UPDATESCORE, ENERGIZER_POINTS);
		this.eat(dot);

		this.heroState = HeroState.Powered;
		this.poweredAccumulator = 0;
		sceneEvents.emit(EVENT_PACMAN_HASPOWER);
	}

	eatFruit(fruit: Phaser.Physics.Arcade.Sprite) {
		const name = fruit.name as keyof typeof Fruit;
		const scoreText = this.scene.add.text(
			fruit.x,
			fruit.y,
			`${Fruit[name]}`,
			{
				color: red.rgba,
				fontSize: '16px',
				fontFamily: 'Emulogic',
				align: 'center',
			});


		sceneEvents.emit(EVENT_GAME_UPDATESCORE, Fruit[name]);
		this.scene.time.delayedCall(500, () => {
			scoreText.destroy(true);
		});

		this.audioSprite.play('eat0');
		fruit.destroy(true);
	}

	die() {
		this.play('hero-die');
		this.audioSprite.play('pacman death');
	}

	preUpdate(t: number, dt: number) {
		super.preUpdate(t, dt);

		this.scene.physics.world.wrapObject(this, 64);

		if (this.nextSnap !== null && this.snapFrameCounter++ > 2) {
			const { move, pos } = this.nextSnap;
			if (this.currentDirection !== move) {
				this.setPosition(pos.x, pos.y);
			}
			this.changeDirection(move);
			this.nextSnap = null;
			this.snapFrameCounter = 0;
		}

		if (this.heroState === HeroState.Normal) {
			return;
		}

		this.poweredAccumulator += dt;
		if (this.poweredAccumulator >= GHOST_SCARE_TIMER) {
			this.heroState = HeroState.Normal;
			this.poweredAccumulator = 0;
		}

	}

	handleMovement(
		pad: Phaser.Input.Gamepad.Gamepad,
		cursors: Phaser.Types.Input.Keyboard.CursorKeys,
		boardLayer: Phaser.Tilemaps.TilemapLayer,
		door: Phaser.GameObjects.Rectangle,
	) {
		if (!Phaser.Geom.Rectangle.Contains(this.scene.physics.world.bounds, this.x, this.y)) {
			// don't switch direction when outside of world; being wrapped
			return;
		}


		const vel = this.body.velocity.lengthSq();

		if (vel > 0.0) {
			this.play('hero-move', true);
		} else {
			this.play('hero-idle');
		}

		const keyDown = pad ? this.getGamepadDownState(pad) : this.getKeysDownState(cursors);

		if (keyDown !== Moves.None) {

			const offset = vel > 0.0 ? 2 : 1;
			const sensorPos = new Phaser.Math.Vector2(this)
				.add(new Phaser.Math.Vector2(offset)
					.multiply(MoveMap[this.currentDirection]));

			const collisionTilePos = new Phaser.Math.Vector2({ // round to closest tile
				x: Math.round(sensorPos.x / tileSize) * tileSize,
				y: Math.round(sensorPos.y / tileSize) * tileSize
			});

			const pos = collisionTilePos.clone().add(new Phaser.Math.Vector2(tileSize * 2)
				.multiply(MoveMap[keyDown]));

			this.sensor.setPosition(pos.x, pos.y);

			if (this.currentDirection !== keyDown && this.snapFrameCounter === 0) {
				if (boardCollision(this.sensor.getBounds(), boardLayer, door)) {

					this.nextSnap = {
						move: keyDown,
						pos: collisionTilePos
					};
				}
			}
		}
	}

	private getKeysDownState(cursors: Phaser.Types.Input.Keyboard.CursorKeys) {
		const { left, right, up, down } = cursors;
		const keyList = [left, right, up, down];
		if (keyList.filter(k => k?.isDown).length > 1) {
			return Moves.None;
		}
		return keyList.find(k => k.isDown)?.keyCode || Moves.None;
	}

	private getGamepadDownState(pad: Phaser.Input.Gamepad.Gamepad) {
		if (this.turningDisabled > 0) {
			this.turningDisabled = this.turningDisabled - 1;
			return this.moveDirection;
		}

		const [x, y] = pad.axes;

		const xDir = Math.round(x.value).toFixed(0);
		const yDir = Math.round(y.value).toFixed(0);

		const xMove = {
			'-1': Moves.Left,
			'0': Moves.None,
			'1': Moves.Right
		}[xDir];

		const yMove = {
			'-1': Moves.Up,
			'0': Moves.None,
			'1': Moves.Down
		}[yDir];

		// Get all currently pressed moves
		const moves = [xMove, yMove].filter(move => move && move !== Moves.None);
		this.moveDirection = moves.find(move => move !== this.moveDirection) || this.moveDirection;

		return this.moveDirection;
	}

	public changeDirection(move: Moves) {
		this.turningDisabled = 3;
		this.currentDirection = move;
		this.moveDirection = move;
		const dir = MoveMap[move];
		this.setVelocity(dir.x * this.speed, dir.y * this.speed);
		this.setRotation(Phaser.Math.Angle.BetweenPoints(MoveMap[Moves.None], dir));
	}
}

Phaser.GameObjects.GameObjectFactory.register('hero', function (
	this: Phaser.GameObjects.GameObjectFactory,
	pos: Required<Phaser.Types.Math.Vector2Like>,
	texture: string,
	level: number
) {
	const hero = new Hero(this.scene, pos, texture, level);

	this.displayList.add(hero);
	this.updateList.add(hero);

	this.scene.physics.world.enableBody(hero, Phaser.Physics.Arcade.DYNAMIC_BODY);

	hero.changeDirection(Moves.Left);
	(hero.body as Phaser.Physics.Arcade.Body).setFriction(0, 0);

	return hero;
});

declare global {
	namespace Phaser.GameObjects {
		interface GameObjectFactory {
			hero(pos: Required<Phaser.Types.Math.Vector2Like>, texture: string, level: number): Hero
		}
	}
}
